import posthog from 'posthog-js';

import { type AnalyticsContext } from './analytics-context';
import {
  type AnalyticsEvent,
  type AnalyticsEvents,
  type AnalyticsPersonProperties,
} from './events';

export interface CaptureOptions<
  TEvent extends AnalyticsEvent = AnalyticsEvent,
> {
  /**
   * Flushes the event before a full navigation tears the page down, which would
   * otherwise drop it from the batched queue.
   */
  sendInstantly?: boolean;
  /**
   * Set on the person the event is attributed to. Only an event declared in
   * `AnalyticsPersonProperties` can set any, and only the keys it declares.
   */
  personProperties?: TEvent extends keyof AnalyticsPersonProperties
    ? AnalyticsPersonProperties[TEvent]
    : never;
}

const toPostHogOptions = <TEvent extends AnalyticsEvent>({
  sendInstantly,
  personProperties,
}: CaptureOptions<TEvent> = {}) => {
  if (!sendInstantly && !personProperties) return undefined;
  return {
    ...(sendInstantly && { send_instantly: true }),
    // Cloned because posthog-js drops any object it has already seen in the
    // payload, so a value shared with the event properties would vanish here.
    ...(personProperties && { $set: structuredClone(personProperties) }),
  };
};

/**
 * Reports one event. Prefer `useAnalytics`, which attaches identity; reach for
 * this directly only where no auth context exists to read, such as the sign-in
 * screens. A missing or failing client never reaches the action itself.
 */
export const captureEvent = <TEvent extends AnalyticsEvent>(
  event: TEvent,
  properties: AnalyticsEvents[TEvent] & Partial<AnalyticsContext>,
  options?: CaptureOptions<TEvent>,
) => {
  try {
    posthog.capture(event, properties, toPostHogOptions(options));
  } catch {
    return;
  }
};
